<template>

  <aside class="project-sidebar" :class="{ collapsed: collapsed }">

    <div class="project-sidebar-header">

      <div v-if="!collapsed" class="d-flex align-items-center justify-content-between w-100">

        <h6 class="mb-0 font-weight-bold">

          <feather-icon icon="CheckSquareIcon" size="15" class="mr-50" />Tasks

        </h6>

        <div class="project-sidebar-actions">

          <b-button

            v-if="canCreate"

            aria-label="New project"

            size="sm"

            variant="outline-secondary"

            class="project-sidebar-icon-btn"

            @click="$emit('create-project')"

          >

            <feather-icon icon="PlusIcon" size="14" />

          </b-button>

          <b-button

            aria-label="Collapse"

            size="sm"

            variant="outline-secondary"

            class="project-sidebar-icon-btn"

            @click="collapsed = true"

          >

            <feather-icon icon="ChevronsLeftIcon" size="14" />

          </b-button>

        </div>

      </div>

      <b-button

        v-else

        aria-label="Expand sidebar"

        size="sm"

        variant="outline-secondary"

        class="project-sidebar-icon-btn mx-auto"

        @click="collapsed = false"

      >

        <feather-icon icon="ChevronsRightIcon" size="14" />

      </b-button>

    </div>



    <div v-if="!collapsed" class="project-sidebar-body">

      <nav class="project-primary-nav" aria-label="Task navigation">

        <button

          type="button"

          class="project-nav-item"

          :class="{ active: navMode === 'tasks' && selectedProjectId == null }"

          @click="$emit('nav-tasks')"

        >

          <feather-icon icon="LayersIcon" size="14" class="mr-75 flex-shrink-0" />

          <span class="project-nav-label">All Tasks</span>

          <div class="ml-auto d-flex align-items-center flex-shrink-0" style="gap: 0.25rem;">
            <b-badge
              v-if="myOpenProjectTaskCount"
              variant="light-info"
              pill
            >
              {{ myOpenProjectTaskCount }} project
            </b-badge>
            <b-badge variant="light-secondary" pill>{{ totalTaskCount }}</b-badge>
          </div>

        </button>



        <button

          type="button"

          class="project-nav-item"

          :class="{ active: navMode === 'projects' }"

          @click="$emit('nav-projects')"

        >

          <feather-icon icon="FolderIcon" size="14" class="mr-75 flex-shrink-0" />

          <span class="project-nav-label">Projects</span>

          <b-badge variant="light-secondary" pill class="ml-auto">{{ visibleProjects.length }}</b-badge>

        </button>

      </nav>



      <div v-if="navMode === 'tasks'" class="project-sidebar-section">

        <div class="project-sidebar-section-label">Filter by project</div>



        <div v-if="loading" class="text-muted small text-center py-2">Loading...</div>



        <div v-else-if="!visibleProjects.length" class="project-empty-state">

          <feather-icon icon="FolderPlusIcon" size="24" class="text-muted mb-50" />

          <p class="small text-muted mb-75">Group tasks into projects and assign team roles.</p>

          <b-button v-if="canCreate" size="sm" variant="primary" @click="$emit('create-project')">

            Create Project

          </b-button>

        </div>



        <div v-else class="project-list">

          <button

            v-for="project in visibleProjects"

            :key="project.id"

            type="button"

            class="project-nav-item project-nav-item--compact"

            :class="{ selected: navMode === 'tasks' && String(selectedProjectId) === String(project.id) }"

            @click="$emit('select-project', project.id)"

          >

            <span

              class="project-color-dot flex-shrink-0"

              :style="{ backgroundColor: project.color || '#7367F0' }"

            />

            <div class="project-nav-label text-left min-width-0">

              <div class="text-truncate font-weight-bold small">{{ project.name }}</div>

              <span class="text-muted project-nav-meta">

                {{ taskCountFor(project.id) }} tasks

              </span>

            </div>

          </button>

        </div>

      </div>



      <div v-else class="project-sidebar-section">

        <div class="project-sidebar-section-label">Your projects</div>



        <div v-if="loading" class="text-muted small text-center py-2">Loading...</div>



        <div v-else-if="!visibleProjects.length" class="project-empty-state">

          <feather-icon icon="FolderPlusIcon" size="24" class="text-muted mb-50" />

          <p class="small text-muted mb-75">Create a project to organize tasks and teams.</p>

          <b-button v-if="canCreate" size="sm" variant="primary" @click="$emit('create-project')">

            Create Project

          </b-button>

        </div>



        <div v-else class="project-list">

          <button

            v-for="project in visibleProjects"

            :key="project.id"

            type="button"

            class="project-nav-item project-nav-item--compact"

            :class="{ selected: navMode === 'projects' && String(selectedProjectId) === String(project.id) }"

            @click="$emit('select-project', project.id)"

          >

            <span

              class="project-color-dot flex-shrink-0"

              :style="{ backgroundColor: project.color || '#7367F0' }"

            />

            <div class="project-nav-label text-left min-width-0">

              <div class="text-truncate font-weight-bold small">{{ project.name }}</div>

              <div class="d-flex align-items-center flex-wrap project-nav-meta-row">

                <b-badge :variant="`light-${statusVariant(project.status)}`" class="project-status-badge">

                  {{ project.status || 'Active' }}

                </b-badge>

                <span class="text-muted project-nav-meta">

                  {{ memberCountFor(project) }} members

                </span>

              </div>

            </div>

            <b-dropdown

              variant="link"

              no-caret

              toggle-class="p-0 project-item-menu"

              boundary="window"

              :popper-opts="{ positionFixed: true }"

              right

              @click.native.stop

            >

              <template #button-content>

                <feather-icon icon="MoreVerticalIcon" size="14" />

              </template>

              <b-dropdown-item @click="$emit('edit-project', project)">

                <feather-icon icon="EditIcon" size="13" class="mr-50" />Settings &amp; Team

              </b-dropdown-item>

            </b-dropdown>

          </button>

        </div>

      </div>

    </div>

  </aside>

</template>



<script>

import {

  BBadge, BButton, BDropdown, BDropdownItem,

} from 'bootstrap-vue'

import {

  normalizeProjectStatus,

  projectStatusVariant,

  projectMemberCount,

} from '@/utils/project-config'

import { visibleProjects as filterVisibleProjects, canCreateProject } from '@/utils/project-permissions'
import { countOpenProjectResponsibilities } from '@/utils/project-responsibility-tasks'
import { getEmpId } from '@/utils/task-permissions'



export default {

  name: 'ProjectSidebar',

  components: {

    BBadge, BButton, BDropdown, BDropdownItem,

  },

  props: {

    projects: { type: Array, default: () => [] },

    tasks: { type: Array, default: () => [] },

    /** Scope-filtered tasks for sidebar count badges — see TaskBoard `sidebarCountTasks`. */
    countTasks: { type: Array, default: () => [] },

    selectedProjectId: { type: [String, Number], default: null },

    navMode: {

      type: String,

      default: 'tasks',

      validator: v => v === 'tasks' || v === 'projects',

    },

    currentUser: { type: Object, default: () => ({}) },

    assignableUsers: { type: Array, default: () => [] },

    loading: { type: Boolean, default: false },

  },

  data() {

    return { collapsed: false }

  },

  computed: {

    visibleProjects() {

      return filterVisibleProjects(this.projects, this.currentUser, this.tasks)

    },

    totalTaskCount() {

      return (this.countTasks || []).length

    },

    myOpenProjectTaskCount() {

      const empId = getEmpId(this.currentUser)

      return countOpenProjectResponsibilities(this.visibleProjects, empId)

    },

    canCreate() {

      return canCreateProject()

    },

  },

  methods: {

    statusVariant(status) {

      return projectStatusVariant(normalizeProjectStatus({ status }))

    },

    taskCountFor(projectId) {

      return (this.countTasks || []).filter(t => {

        const pid = t.projectId ?? t.project_id

        return pid != null && String(pid) === String(projectId)

      }).length

    },

    memberCountFor(project) {

      return projectMemberCount(project)

    },

  },

}

</script>



<style lang="scss" scoped>

.min-width-0 {

  min-width: 0;

}

</style>

